import { useState, useEffect, useCallback } from 'react';
import { api, handleAuthFailure } from '../data/api';
import Badge from '../components/Badge';
import Btn from '../components/Btn';
import { TableWrap, TH, TD } from '../components/TableWrap';
import { useTheme } from "../context/ThemeContext";

export default function LogViewer() {
  const { mode } = useTheme();
  const [logs, setLogs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [filterAff, setFilterAff]     = useState("");
  const [filterDev, setFilterDev]     = useState("");
  const [filterSev, setFilterSev]     = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]           = useState("");
  const [selected, setSelected]       = useState(null);

  const selectStyle = {
    background: "var(--surface)",
    border: `1px solid var(--border)`,
    borderRadius: 6,
    padding: "7px 10px",
    fontSize: 12,
    color: "var(--text)",
    fontFamily: "inherit",
    outline: "none",
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterAff) params.affiliate_id = filterAff;
      if (filterDev) params.device_id    = filterDev;
      if (filterSev) params.severity     = filterSev;
      if (search)    params.search       = search;
      const rows = await api.logs.list(params);
      setLogs(rows);
      setSelected(prev => prev ? (rows.find(r => r.id === prev.id) ?? null) : null);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError(e.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [filterAff, filterDev, filterSev, search]);

  useEffect(() => { load(); }, [load]);

  const handleApply = () => setSearch(searchInput);
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleApply(); };

  const exportCSV = () => {
    if (!logs.length) return;
    const cols = ['recorded_at', 'affiliate_id', 'device_id', 'severity', 'source', 'message', 'app_ver'];
    const header = cols.join(',');
    const rows = logs.map(l =>
      cols.map(c => `"${(l[c] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    );
    const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const affIds = [...new Set(logs.map(l => l.affiliate_id).filter(Boolean))].sort();
  const devIds = [...new Set(logs.map(l => l.device_id).filter(Boolean))].sort();

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:14, borderBottom:`1px solid var(--border)` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <h1 style={{ fontSize:17, fontWeight:700, color:"var(--ink)" }}>Log Viewer</h1>
          {loading && <span style={{ fontSize:11, color:"var(--text-dim)" }}>Loading…</span>}
          {!loading && !error && (
            <span style={{ fontSize:11, color:"var(--text-dim)" }}>
              {logs.length} entr{logs.length === 1 ? 'y' : 'ies'}
            </span>
          )}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn onClick={load}>↻ Refresh</Btn>
          <Btn onClick={exportCSV}>Export CSV</Btn>
        </div>
      </div>

      {error && (
        <div style={{ background:"var(--red-bg,#fef2f2)", border:"1px solid var(--red-border,#fca5a5)", borderRadius:7, padding:"10px 14px", fontSize:12, color:"var(--red,#dc2626)", marginBottom:14 }}>
          {error}
        </div>
      )}

      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        <select value={filterAff} onChange={e => setFilterAff(e.target.value)} style={selectStyle}>
          <option value="">All Affiliates</option>
          {affIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>

        <select value={filterDev} onChange={e => setFilterDev(e.target.value)} style={selectStyle}>
          <option value="">All Devices</option>
          {devIds.map(id => <option key={id} value={id}>{id}</option>)}
        </select>

        <select value={filterSev} onChange={e => setFilterSev(e.target.value)} style={selectStyle}>
          <option value="">All Severity</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>

        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search messages…"
          style={{ ...selectStyle, flex:1, minWidth:160 }}
        />
        <Btn primary onClick={handleApply}>Apply</Btn>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 264px", gap:10 }}>
        <TableWrap>
          <thead>
            <tr>
              <TH>Timestamp</TH>
              <TH>Affiliate</TH>
              <TH>Device</TH>
              <TH>Severity</TH>
              <TH>Message</TH>
            </tr>
          </thead>
          <tbody>
            {logs.length ? logs.map((log) => (
              <tr
                key={log.id}
                onClick={() => setSelected(log)}
                style={{ cursor:"pointer", background: selected?.id === log.id ? "var(--surface-alt)" : "transparent" }}
              >
                <TD mono sub style={{ whiteSpace:"nowrap", fontSize:11 }}>{fmt(log.recorded_at)}</TD>
                <TD mono sub style={{ fontSize:11 }}>{log.affiliate_id ?? '—'}</TD>
                <TD mono style={{ fontSize:11 }}>{log.device_id ?? '—'}</TD>
                <TD><Badge status={log.severity} /></TD>
                <TD style={{ maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{log.message}</TD>
              </tr>
            )) : (
              <tr>
                <td colSpan={5} style={{ padding:"28px 14px", textAlign:"center", fontSize:12, color:"var(--text-dim)" }}>
                  {loading ? 'Loading…' : 'No entries match the current filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </TableWrap>

        <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, padding:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Log Detail</div>
          {selected ? (
            <>
              {[
                ["Timestamp",   fmt(selected.recorded_at), true],
                ["Affiliate",   selected.affiliate_id,     true],
                ["Device",      selected.device_id,        true],
                ["Model",       selected.device_model,     false],
                ["Severity",    null,                      false],
                ["Source",      selected.source,           false],
                ["App Version", selected.app_ver,          true],
              ].map(([k, v, mono]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:`1px solid var(--border)` }}>
                  <span style={{ fontSize:11, color:"var(--text-sub)" }}>{k}</span>
                  {k === "Severity"
                    ? <Badge status={selected.severity} />
                    : <span style={{ fontSize:11, fontWeight:500, color:"var(--ink)", fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit" }}>{v ?? '—'}</span>
                  }
                </div>
              ))}
              <div style={{ paddingTop:10 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Message</div>
                <div style={{ fontSize:12, color:"var(--text)", lineHeight:1.55 }}>{selected.message}</div>
              </div>
            </>
          ) : (
            <div style={{ textAlign:"center", paddingTop:50, color:"var(--text-dim)", fontSize:12 }}>
              <div style={{ fontSize:18, marginBottom:8 }}>≡</div>
              Select a log entry<br/>to view details
            </div>
          )}
        </div>
      </div>
    </>
  );
}
